import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  QueryId,
  TOTAL_PRICE_DEPENDENT_QUERIES,
} from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseMutationAddPreBookingPickupPointData =
  Booking.Components['PutPickupPointResponse'];

interface UseMutationAddPreBookingPickupPointError {
  errors: any;
  message?: string;
}

interface UseMutationAddPreBookingPickupPointParameters {
  brand?: Content.Properties.Brand;
  data: Booking.Components['UniquePaxHashesBodyRequest'];
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
  pickupPointId: number;
}

const useMutationAddPreBookingPickupPoint = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    UseMutationAddPreBookingPickupPointData,
    UseMutationAddPreBookingPickupPointError,
    UseMutationAddPreBookingPickupPointParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/pickup-point/add-pickup-point',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            data: parameters.data,
            pickupPointId: parameters.pickupPointId,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'PUT',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error(
            'bookingFlow.useMutationAddPreBookingPickupPoint',
            error,
          );

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (_data, parameters) => {
        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });

        TOTAL_PRICE_DEPENDENT_QUERIES.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationAddPreBookingPickupPoint;
